import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { 
  getProductsForFoodDiscovery, 
  getProductsBySeller, 
  addProduct,
  updateProduct,
  deleteProduct 
} from '../../utils/productApi';

/**
 * React Query hooks for product operations
 * Provides automatic caching, loading states, and error handling
 */

// Query keys for products
export const productKeys = {
  all: ['products'],
  lists: () => [...productKeys.all, 'list'],
  list: (filters) => [...productKeys.lists(), filters],
  details: () => [...productKeys.all, 'detail'],
  detail: (id) => [...productKeys.details(), id],
  seller: (sellerId) => [...productKeys.all, 'seller', sellerId],
  discovery: () => [...productKeys.all, 'discovery'],
};

/**
 * Hook to fetch products for food discovery
 */
export function useProductsForDiscovery(options = {}) {
  return useQuery({
    queryKey: productKeys.discovery(),
    queryFn: getProductsForFoodDiscovery,
    staleTime: 2 * 60 * 1000, // 2 minutes
    cacheTime: 5 * 60 * 1000, // 5 minutes
    ...options,
  });
}

/**
 * Hook to fetch products by seller
 */
export function useProductsBySeller(sellerId, options = {}) {
  return useQuery({
    queryKey: productKeys.seller(sellerId),
    queryFn: () => getProductsBySeller(sellerId),
    enabled: !!sellerId,
    staleTime: 10 * 60 * 1000, // 10 minutes - increased to reduce refetches
    cacheTime: 15 * 60 * 1000, // 15 minutes - increased cache time
    refetchOnWindowFocus: false, // Prevent refetch on window focus
    refetchOnMount: false, // Prevent refetch on component mount if data exists
    ...options,
  });
}

/**
 * Hook to fetch available products by seller (for customer-facing storefronts)
 */
export function useAvailableProductsBySeller(sellerId, options = {}) {
  return useQuery({
    queryKey: [...productKeys.seller(sellerId), 'available'],
    queryFn: () => getProductsBySeller(sellerId, { filterAvailable: true }),
    enabled: !!sellerId,
    staleTime: 5 * 60 * 1000, // 5 minutes - shorter cache for customer-facing data
    cacheTime: 10 * 60 * 1000, // 10 minutes
    refetchOnWindowFocus: false,
    refetchOnMount: true, // Always refetch on mount for customer-facing data
    ...options,
  });
}

/**
 * Hook to fetch a single product by ID
 */
export function useProduct(productId, options = {}) {
  return useQuery({
    queryKey: productKeys.detail(productId),
    queryFn: async () => {
      // This would need to be implemented in productApi
      const products = await getProductsForFoodDiscovery();
      return products.find(p => p.id === productId);
    },
    enabled: !!productId,
    staleTime: 5 * 60 * 1000, // 5 minutes
    cacheTime: 10 * 60 * 1000, // 10 minutes
    ...options,
  });
}

/**
 * Hook to add a new product
 */
export function useAddProduct() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: addProduct,
    onSuccess: (newProduct) => {
      // Invalidate and refetch seller products
      if (newProduct.sellerId) {
        queryClient.invalidateQueries({
          queryKey: productKeys.seller(newProduct.sellerId),
        });
      }
      
      // Invalidate discovery products
      queryClient.invalidateQueries({
        queryKey: productKeys.discovery(),
      });
      
      // Add the new product to the cache
      queryClient.setQueryData(
        productKeys.detail(newProduct.id),
        newProduct
      );
    },
    onError: (error) => {
      console.error('Failed to add product:', error);
    },
  });
}

/**
 * Hook to update an existing product
 */
export function useUpdateProduct() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ productId, updates }) => updateProduct(productId, updates),
    onSuccess: (updatedProduct) => {
      // Update the product in all relevant caches
      queryClient.setQueryData(
        productKeys.detail(updatedProduct.id),
        updatedProduct
      );
      
      // Invalidate seller products to refetch the updated list
      if (updatedProduct.sellerId) {
        queryClient.invalidateQueries({
          queryKey: productKeys.seller(updatedProduct.sellerId),
        });
      }
      
      // Invalidate discovery products
      queryClient.invalidateQueries({
        queryKey: productKeys.discovery(),
      });
    },
    onError: (error) => {
      console.error('Failed to update product:', error);
    },
  });
}

/**
 * Hook to delete a product
 */
export function useDeleteProduct() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: deleteProduct,
    onSuccess: (deletedProduct) => {
      // Remove the product from all caches
      queryClient.removeQueries({
        queryKey: productKeys.detail(deletedProduct.id),
      });
      
      // Invalidate seller products to refetch the updated list
      if (deletedProduct.sellerId) {
        queryClient.invalidateQueries({
          queryKey: productKeys.seller(deletedProduct.sellerId),
        });
      }
      
      // Invalidate discovery products
      queryClient.invalidateQueries({
        queryKey: productKeys.discovery(),
      });
    },
    onError: (error) => {
      console.error('Failed to delete product:', error);
    },
  });
}

/**
 * Hook to reorder products (optimistic update)
 */
export function useReorderProducts() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ sellerId, newOrder }) => {
      // This would be implemented in productApi
      // For now, just return the new order
      return newOrder;
    },
    onMutate: async ({ sellerId, newOrder }) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({
        queryKey: productKeys.seller(sellerId),
      });
      
      // Snapshot the previous value
      const previousProducts = queryClient.getQueryData(
        productKeys.seller(sellerId)
      );
      
      // Optimistically update to the new value
      queryClient.setQueryData(
        productKeys.seller(sellerId),
        newOrder
      );
      
      // Return a context object with the snapshotted value
      return { previousProducts };
    },
    onError: (err, { sellerId }, context) => {
      // If the mutation fails, use the context returned from onMutate to roll back
      if (context?.previousProducts) {
        queryClient.setQueryData(
          productKeys.seller(sellerId),
          context.previousProducts
        );
      }
    },
    onSettled: (data, error, { sellerId }) => {
      // Always refetch after error or success
      queryClient.invalidateQueries({
        queryKey: productKeys.seller(sellerId),
      });
    },
  });
}
